import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * /setup — Bring-Your-Own-Keys wizard.
 * Route: /setup   (owner-authed inside the paired app)
 *
 * Reads the 29-key catalog from GET /api/setup/integrations, the user&apos;s
 * current keys (masked) from GET /api/setup/my_keys, and writes with
 * PUT/DELETE /api/setup/my_keys/{name}. Everything is per-device: two
 * different paired devices can each keep their own keys.
 */
export default function Setup() {
  const [catalog, setCatalog] = useState([]);
  const [myKeys, setMyKeys] = useState({});   // key_name → {present, masked, updated_at}
  const [openCat, setOpenCat] = useState({}); // category → bool
  const [drafts, setDrafts] = useState({});   // key_name → plaintext being typed
  const [savingKey, setSavingKey] = useState("");
  const [status, setStatus] = useState(null);

  const loadCatalog = async () => {
    try {
      const r = await api.get("/setup/integrations");
      setCatalog(r.data.catalog || []);
    } catch (e) { console.warn("catalog fetch failed", e?.message); }
  };
  const loadMine = async () => {
    try {
      const r = await api.get("/setup/my_keys");
      setMyKeys(r.data.keys || {});
    } catch { /* ignore */ }
  };
  const loadStatus = async () => {
    try {
      const r = await api.get("/setup/status");
      setStatus(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadCatalog(); loadMine(); loadStatus(); }, []);

  const filled = useMemo(() => new Set(Object.keys(myKeys)), [myKeys]);

  const save = async (name) => {
    const val = (drafts[name] || "").trim();
    if (!val) return;
    setSavingKey(name);
    try {
      await api.put(`/setup/my_keys/${name}`, { value: val });
      setDrafts((d) => ({ ...d, [name]: "" }));
      await Promise.all([loadMine(), loadStatus()]);
    } catch (e) { alert(e?.response?.data?.detail || "save failed"); }
    finally { setSavingKey(""); }
  };
  const remove = async (name) => {
    if (!window.confirm(`Delete your ${name} key?`)) return;
    try {
      await api.delete(`/setup/my_keys/${name}`);
      await Promise.all([loadMine(), loadStatus()]);
    } catch (e) { alert(e?.response?.data?.detail || "delete failed"); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#f5f0dc", fontFamily: "'Inter',system-ui,sans-serif" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
        <a href="/" style={{ color: "#ffb800", fontSize: 12, letterSpacing: "0.2em", textDecoration: "none" }}>← BACK TO ORB</a>
        <h1 style={{ fontSize: "clamp(32px,5vw,52px)", color: "#ffb800", fontWeight: 800, marginTop: 24, marginBottom: 8 }}>Setup — your keys, your control</h1>
        <p style={{ opacity: 0.75, lineHeight: 1.6, marginTop: 0, marginBottom: 24 }}>
          Paste any keys, IPs, or email addresses below to link RAW Data
          into <b>your</b> accounts. The app runs on our shared UK backend,
          but each integration uses <b>your</b> keys — so it&apos;s your
          Gmail, your Capital.com account, your smart plugs. We store your
          keys per-device; two paired devices each keep their own vault.
        </p>

        {/* Progress ring */}
        {status && (
          <div data-testid="setup-status" style={{ display: "flex", alignItems: "center", gap: 16, padding: 16, background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.25)", borderRadius: 8, marginBottom: 24 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#ffb800" }}>{status.percent}%</div>
            <div style={{ flex: 1, fontSize: 13, opacity: 0.85 }}>
              <b>{status.filled}</b> of {status.total} keys set · vault <code style={{ opacity: 0.6 }}>{status.vault_id}</code>
              {status.required_missing?.length > 0 && (
                <div style={{ fontSize: 11, color: "#ffb800", opacity: 0.85, marginTop: 4 }}>
                  Required still missing: {status.required_missing.join(", ")}
                </div>
              )}
            </div>
          </div>
        )}

        {catalog.map((cat) => {
          const isOpen = openCat[cat.category] !== false;
          const filledCount = cat.keys.filter(k => filled.has(k.key)).length;
          return (
            <div key={cat.category} style={{ marginBottom: 12, background: "rgba(255,184,0,0.02)", border: "1px solid rgba(255,184,0,0.15)", borderRadius: 8, overflow: "hidden" }}>
              <button
                data-testid={`setup-cat-${cat.category}`}
                onClick={() => setOpenCat((o) => ({ ...o, [cat.category]: !isOpen }))}
                style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", color: "#f5f0dc", padding: "16px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: "#ffb800" }}>{cat.category}</span>
                <span style={{ fontSize: 11, opacity: 0.6 }}>{filledCount} / {cat.total_keys} · {isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div style={{ padding: "0 18px 18px" }}>
                  <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 14, lineHeight: 1.5 }}>{cat.blurb}</div>
                  {cat.keys.map((k) => {
                    const mine = myKeys[k.key];
                    const draft = drafts[k.key] || "";
                    const isSaving = savingKey === k.key;
                    return (
                      <div key={k.key} data-testid={`setup-key-${k.key}`} style={{ marginBottom: 18, padding: 12, background: "rgba(0,0,0,0.35)", borderRadius: 6, border: `1px solid ${mine ? "rgba(74,222,128,0.4)" : "rgba(255,184,0,0.15)"}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 700 }}>{k.label}</span>
                            {k.required && <span style={{ marginLeft: 8, fontSize: 9, background: "#ffb800", color: "#000", padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>REQUIRED</span>}
                            {mine && <span style={{ marginLeft: 8, fontSize: 10, color: "#4ade80" }}>✓ set · {mine.masked}</span>}
                          </div>
                          {k.get_url && <a href={k.get_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#ffb800", textDecoration: "underline" }}>get key ↗</a>}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6, lineHeight: 1.5 }}>{k.why}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                          <input
                            data-testid={`setup-input-${k.key}`}
                            type={k.sensitive ? "password" : "text"}
                            value={draft}
                            placeholder={k.hint || (mine ? "Paste to replace…" : "Paste key here")}
                            onChange={(e) => setDrafts((d) => ({ ...d, [k.key]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") save(k.key); }}
                            style={{ flex: 1, padding: "8px 10px", fontSize: 12, background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.3)", color: "#f5f0dc", borderRadius: 4, outline: "none", fontFamily: "monospace" }}
                          />
                          <button
                            data-testid={`setup-save-${k.key}`}
                            onClick={() => save(k.key)}
                            disabled={!draft.trim() || isSaving}
                            style={{ background: "#ffb800", color: "#000", padding: "8px 16px", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", border: "none", borderRadius: 4, cursor: draft.trim() ? "pointer" : "not-allowed", opacity: draft.trim() ? 1 : 0.4 }}
                          >{isSaving ? "…" : "SAVE"}</button>
                          {mine && (
                            <button
                              data-testid={`setup-delete-${k.key}`}
                              onClick={() => remove(k.key)}
                              style={{ background: "transparent", color: "#ef4444", border: "1px solid #ef4444", padding: "8px 12px", fontSize: 11, borderRadius: 4, cursor: "pointer" }}
                            >✕</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {catalog.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", opacity: 0.5 }}>Loading catalog…</div>
        )}

        <div style={{ marginTop: 32, padding: 16, background: "rgba(255,184,0,0.04)", border: "1px solid rgba(255,184,0,0.2)", borderRadius: 8, fontSize: 12, opacity: 0.85, lineHeight: 1.6 }}>
          <b>Wake word:</b> say <b style={{ color: "#ffb800" }}>&quot;Raw Company&quot;</b> anywhere in the app to wake the assistant.
          <br/>
          <b>Your privacy:</b> your keys never leave the RAW Data backend and are never used to touch anyone else&apos;s account. Delete a key any time — the feature just goes dark until you add another.
        </div>
      </div>
    </div>
  );
}
